import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   Input,
   OnChanges,
   OnDestroy,
   OnInit,
   SimpleChanges,
   ViewEncapsulation,
} from '@angular/core';
import { ControlContainer, UntypedFormGroup } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { CallState } from '@lcs/shared/api';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PrivilegeView } from 'src/app/pages/role-details/models/role-detail-view.model';
import { GetUserDetail_user_rolesConnection_items } from 'src/app/pages/user-details/queries/generated/GetUserDetail';
import { ConfirmationDialogService } from '../confirmation-dialog/confirmation-dialog.service';
import { DialogSize } from '../confirmation-dialog/enumerations/dialog-size.enum';
import { ConfirmationDialogType } from '../confirmation-dialog/models/confirmation-dialog.config';

interface PrivilegeRoleInfo {
   roleNames: string[];
}
@Component({
   changeDetection: ChangeDetectionStrategy.OnPush,
   encapsulation: ViewEncapsulation.None,
   selector: 'ndt-privileges-list',
   templateUrl: './privileges-list.component.html',
   styleUrls: ['./privileges-list.component.scss'],
   standalone: false,
})
export class PrivilegeListComponent implements OnInit, OnChanges, OnDestroy {
   private _privileges: PrivilegeView[];

   private _userRoles: GetUserDetail_user_rolesConnection_items[] = [];

   private dialogIsOpen = false;

   private destroyed$ = new Subject<void>();

   @Input()
   set privileges(privileges: PrivilegeView[]) {
      this._privileges = privileges;
   }

   get privileges(): PrivilegeView[] {
      return this._privileges;
   }

   @Input()
   set userRoles(userRoles: GetUserDetail_user_rolesConnection_items[]) {
      this._userRoles = userRoles;
   }

   get userRoles(): GetUserDetail_user_rolesConnection_items[] {
      return this._userRoles;
   }

   @Input() accountId: number;

   @Input() callState: CallState;

   @Input() isOnSystemPage: boolean;

   form: UntypedFormGroup;

   administrativeFilter = { categoryId: 1 };

   extensionsFilter = { categoryId: 5 };

   entitiesFilter = { categoryId: 6 };

   callHistoryFilter = { categoryId: 7 };

   reportsFilter = { categoryId: 3 };

   setupFilter = { categoryId: 2 };

   systemFilter = { categoryId: 4 };

   privilegesGrantedByRole: { [key: string]: PrivilegeRoleInfo } = {};

   constructor(
      private controlContainer: ControlContainer,
      private changeDetector: ChangeDetectorRef,
      private confirmationService: ConfirmationDialogService
   ) {}

   ngOnInit(): void {
      this.form = <UntypedFormGroup>this.controlContainer.control;
      if (this._userRoles) {
         this.subscribeToFormChangesForRolePrivilegesControls();
         this.updatePrivilegesCheckState();
         this.updatePrivilegesGrantedByRoleMap();
      }
   }

   ngOnChanges(changes: SimpleChanges): void {
      if (changes['userRoles'] && this._userRoles) {
         this.updatePrivilegesGrantedByRoleMap();
         this.updatePrivilegesCheckState();
      }
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   private updatePrivilegesGrantedByRoleMap(): void {
      this.privilegesGrantedByRole = {};

      this._userRoles.forEach((userRole) => {
         const roleName = userRole.role.name ? userRole.role.name : '';

         userRole.role.rolePrivilegesConnection.items.forEach((rolePrivilege) => {
            ['isAdd', 'isView', 'isEdit', 'isDelete', 'isEnabled'].forEach((permission) => {
               const key = `${rolePrivilege.privilegeId}.${permission}`;

               if (rolePrivilege[permission]) {
                  if (!this.privilegesGrantedByRole[key]) {
                     this.privilegesGrantedByRole[key] = {
                        roleNames: [roleName],
                     };
                  } else {
                     this.privilegesGrantedByRole[key].roleNames.push(roleName);
                  }
               }
            });
         });
      });
   }

   private updatePrivilegesCheckState(): void {
      if (!this.form) return;

      this._userRoles.forEach((userRole) => {
         userRole.role.rolePrivilegesConnection.items.forEach((rolePrivilege) => {
            ['isAdd', 'isView', 'isEdit', 'isDelete', 'isEnabled'].forEach((permission) => {
               if (rolePrivilege[permission]) {
                  const controlPath = `${rolePrivilege.privilegeId}.${permission}`;
                  const control = this.form.get(controlPath);
                  if (control) {
                     control.setValue(true, { emitEvent: false });
                     control.markAsPristine();
                  }
               }
            });
         });
      });
   }

   private subscribeToFormChangesForRolePrivilegesControls(): void {
      Object.keys(this.privilegesGrantedByRole).forEach((key: string) => {
         const controlPath = `${key.toString()}`;
         const control = this.form.get(controlPath);

         if (control) {
            control.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => {
               const privilegeInfo = this.privilegesGrantedByRole[key];

               if (privilegeInfo) {
                  const rolesListString = privilegeInfo.roleNames.join('\n');
                  const message = `This privilege is granted by a role. User must be removed from the following role(s) to remove privilege: \n ${rolesListString}`;

                  const control = this.form.get(controlPath);

                  if (control && control.valueChanges && control.dirty && !control.value) {
                     if (!this.dialogIsOpen) {
                        this.openDialog(message);
                     }
                     control.setValue(true, { emitEvent: false });
                     control.markAsPristine();
                  }
               }
            });
         }
      });
   }

   openDialog(message: string): void {
      this.dialogIsOpen = true;

      const dialogRef = this.confirmationService.confirm({
         data: {
            dialogType: ConfirmationDialogType.Ok,
            header: 'Could not remove privilege',
            message: message,
         },
         size: DialogSize.Medium,
      });

      dialogRef.afterClosed().subscribe(() => {
         this.dialogIsOpen = false;
      });
   }

   getCategoryName(categoryId: number): string {
      switch (categoryId) {
         case 1:
            return 'Administrative Items';
         case 3:
            return 'Reports';
         case 2:
            return 'Setup';
         case 4:
            return 'System Settings';
         case 7:
            return 'Call History';
         case 6:
            return 'Users and Privileges';
         case 5:
            return 'Extensions';
         default:
            return 'Other Settings';
      }
   }

   getPrivilegesByCategory(categoryId: number): PrivilegeView[] {
      return this.privileges.filter(
         (privilege) =>
            privilege.categoryId === categoryId &&
            !privilege.isHidden &&
            (this.isOnSystemPage ? true : !privilege.isSystem)
      );
   }

   hasPermissionInCategory(categoryId: number, permission: string): boolean {
      const privilegesInCategory = this.filterPrivileges(this.getPrivilegesByCategory(categoryId));

      return privilegesInCategory.some((privilege) => privilege[permission]);
   }

   searchText: string = '';

   filterPrivileges(privileges: PrivilegeView[]): PrivilegeView[] {
      return privileges.filter((privilege) =>
         privilege.description?.toLowerCase().includes(this.searchText.toLowerCase())
      );
   }

   onSearchTextChange(searchText: string): void {
      this.searchText = searchText.replace(/\\/g, '');
   }

   selectAllPrivilegesInCategory(categoryId: number, permission: string, event: MatCheckboxChange): void {
      const privilegesInCategory = this.filterPrivileges(this.getPrivilegesByCategory(categoryId));

      privilegesInCategory.forEach((privilege) => {
         if (privilege[permission]) {
            const controlPath = `${privilege.privilegeId}.${permission}`;
            const control = this.form.get(controlPath);

            const privilegeKey = `${privilege.privilegeId}.${permission}`;

            if (control) {
               if (!this.privilegesGrantedByRole[privilegeKey]) {
                  control.setValue(event.checked);
                  control.markAsDirty();
               }

               if (this.privilegesGrantedByRole[privilegeKey]) {
                  if (!this.dialogIsOpen && event.checked === false) {
                     this.showRoleGrantedDialog();
                  }
               }
            }
         }
      });

      this.allPrivilegesInCategoryChecked(categoryId, permission);

      this.changeDetector.detectChanges();
   }

   private showRoleGrantedDialog(): void {
      const message =
         'Some privileges selected are granted by roles. User must be removed from the role to remove privileges. \n \n Select individual privileges to view corresponding role(s).';
      this.openDialog(message);
   }

   allPrivilegesInCategoryChecked(categoryId: number, permission: string): boolean {
      const privilegesInCategory = this.filterPrivileges(this.getPrivilegesByCategory(categoryId));
      const result = privilegesInCategory
         .filter((privilege) => privilege[permission])
         .every((privilege) => {
            const control = this.form.get(privilege.privilegeId + '.' + permission);
            return control ? control.value : false;
         });
      return result;
   }

   selectAllPrivileges(event: MatCheckboxChange): void {
      const privileges = this.filterPrivileges(this.getVisiblePrivileges());

      privileges.forEach((privilege) => {
         const controlNames = ['isAdd', 'isDelete', 'isEdit', 'isEnabled', 'isView'];

         controlNames.forEach((controlName) => {
            if (privilege[controlName]) {
               const controlPath = `${privilege.privilegeId}.${controlName}`;
               const control = this.form.get(controlPath);

               const privilegeKey = `${privilege.privilegeId}.${controlName}`;

               if (control) {
                  if (!this.privilegesGrantedByRole[privilegeKey]) {
                     control.setValue(event.checked);
                     control.markAsDirty();
                  }

                  if (this.privilegesGrantedByRole[privilegeKey]) {
                     if (!this.dialogIsOpen && event.checked === false) {
                        this.showRoleGrantedDialog();
                     }
                  }
               }
            }
         });
      });

      this.changeDetector.detectChanges();
   }

   allPrivilegesChecked(): boolean {
      const isPrivilegeChecked = (type: string) =>
         this.filterPrivileges(this.getVisiblePrivileges())
            .filter((privilege) => privilege[type])
            .every((privilege) => this.form.get(privilege.privilegeId + '.' + type)?.value ?? false);

      return ['isAdd', 'isDelete', 'isEdit', 'isEnabled', 'isView'].every(isPrivilegeChecked);
   }

   private getVisiblePrivileges(): PrivilegeView[] {
      return this.privileges.filter(
         (privilege) => !privilege.isHidden && (this.isOnSystemPage ? true : !privilege.isSystem)
      );
   }
}
